import { useState, useCallback } from "react";
import { useOrganization } from "../context/OrganizationContext";
import { supabase } from "../providers/supabase/supabase";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export type EmailMode = "context-aware" | "custom-prompt" | "manual";

export interface GeneratedEmail {
    subject: string;
    body_html: string;
    body_text: string;
}

interface UseEmailReturn {
    subject: string;
    setSubject: (subject: string) => void;
    bodyHtml: string;
    setBodyHtml: (body: string) => void;
    bodyText: string;
    setBodyText: (body: string) => void;
    mode: EmailMode;
    setMode: (mode: EmailMode) => void;
    customPrompt: string;
    setCustomPrompt: (prompt: string) => void;
    isGenerating: boolean;
    isSending: boolean;
    error: string | null;
    generateAIEmail: () => Promise<void>;
    sendEmail: (toEmail: string, toName?: string) => Promise<boolean>;
    reset: () => void;
    contextInfo: { messagesIncluded: number; lastMessageDate: string | null } | null;
}

export const useEmail = (contactId: number): UseEmailReturn => {
    const { currentOrgId } = useOrganization();

    const [subject, setSubject] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [mode, setMode] = useState<EmailMode>("context-aware");
    const [customPrompt, setCustomPrompt] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contextInfo, setContextInfo] = useState<{ messagesIncluded: number; lastMessageDate: string | null } | null>(null);

    // Helper to get current session token
    const getAuthToken = async (): Promise<string | null> => {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token || null;
    };

    const generateAIEmail = useCallback(async () => {
        if (mode === "manual") return;
        if (mode === "custom-prompt" && !customPrompt.trim()) {
            setError("Please enter a custom prompt");
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            const token = await getAuthToken();
            if (!token) {
                throw new Error("Not authenticated. Please log in.");
            }

            const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-email-generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    mode: mode === "context-aware" ? "context-aware" : "custom-prompt",
                    contact_id: contactId,
                    user_prompt: mode === "custom-prompt" ? customPrompt : undefined,
                    include_history_count: 5,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || "Failed to generate email");
            }

            setSubject(data.email.subject || "");
            setBodyHtml(data.email.body_html || "");
            setBodyText(data.email.body_text || "");

            if (data.context_used) {
                setContextInfo({
                    messagesIncluded: data.context_used.messages_included,
                    lastMessageDate: data.context_used.last_message_date,
                });
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to generate AI email");
            console.error("AI email generation error:", err);
        } finally {
            setIsGenerating(false);
        }
    }, [contactId, mode, customPrompt]);

    const sendEmail = useCallback(async (toEmail: string, toName?: string): Promise<boolean> => {
        if (!subject.trim()) {
            setError("Subject cannot be empty");
            return false;
        }
        if (!bodyHtml.trim() && !bodyText.trim()) {
            setError("Email body cannot be empty");
            return false;
        }
        if (!currentOrgId) {
            setError("No organization selected");
            return false;
        }

        setIsSending(true);
        setError(null);

        try {
            const token = await getAuthToken();
            if (!token) {
                throw new Error("Not authenticated. Please log in.");
            }

            const response = await fetch(`${SUPABASE_URL}/functions/v1/email-send`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    org_id: currentOrgId,
                    contact_id: contactId,
                    to_email: toEmail,
                    to_name: toName,
                    subject: subject.trim(),
                    body_html: bodyHtml || `<p>${bodyText}</p>`,
                    body_text: bodyText || bodyHtml.replace(/<[^>]*>/g, ""),
                    was_ai_generated: mode !== "manual",
                    ai_mode: mode !== "manual" ? mode : undefined,
                    ai_prompt: mode === "custom-prompt" ? customPrompt : undefined,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || "Failed to send email");
            }

            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to send email");
            console.error("Email send error:", err);
            return false;
        } finally {
            setIsSending(false);
        }
    }, [contactId, currentOrgId, subject, bodyHtml, bodyText, mode, customPrompt]);

    const reset = useCallback(() => {
        setSubject("");
        setBodyHtml("");
        setBodyText("");
        setCustomPrompt("");
        setError(null);
        setContextInfo(null);
    }, []);

    return {
        subject,
        setSubject,
        bodyHtml,
        setBodyHtml,
        bodyText,
        setBodyText,
        mode,
        setMode,
        customPrompt,
        setCustomPrompt,
        isGenerating,
        isSending,
        error,
        generateAIEmail,
        sendEmail,
        reset,
        contextInfo,
    };
};
